import React from 'react';
import {AlertCircleIcon, CreditCardIcon, ExternalLinkIcon, XCircleIcon} from '../Icons';
import type {Subscription} from '../../domain/types';

interface SubscriptionManagementCardProps {
    subscription: Subscription;
}

/**
 * Payment method and cancellation deep-link straight into Paddle's hosted management pages —
 * as merchant of record, Paddle owns both flows.
 */
const SubscriptionManagementCard: React.FC<SubscriptionManagementCardProps> = ({subscription}) => {
  const {status, billingCycle, nextBilledAt, managementUrls} = subscription;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const billingCycleText = (() => {
    if (!billingCycle) return 'N/A';
    const {frequency, interval} = billingCycle;
    if (frequency === 1) {
      if (interval === 'month') return 'Monthly';
      if (interval === 'year') return 'Yearly';
      return interval;
    }
    return `Every ${frequency} ${interval}s`;
  })();

  const hasManagementUrls = Boolean(managementUrls.updatePaymentMethod || managementUrls.cancel);

  const statusClass = status === 'active'
    ? 'bg-green-500/20 text-green-400'
    : status === 'trialing'
      ? 'bg-blue-500/20 text-blue-400'
      : status === 'paused'
        ? 'bg-amber-500/20 text-amber-400'
        : 'bg-gray-500/20 text-gray-400';

  return (
    <div className="rounded-lg bg-gray-800 border border-gray-700 p-8">
      <h2 className="text-2xl font-bold text-white mb-2">Subscription Management</h2>
      <p className="text-gray-400 mb-6">
        Your subscription is billed and managed by Paddle, our payment provider.
      </p>

      <div className="space-y-4 mb-6">
        <div className="flex justify-between items-center py-3 border-b border-gray-700">
          <span className="text-gray-300 font-medium">Status</span>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusClass}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        </div>

        <div className="flex justify-between items-center py-3 border-b border-gray-700">
          <span className="text-gray-300 font-medium">Billing Cycle</span>
          <span className="text-white">{billingCycleText}</span>
        </div>

        {nextBilledAt && (
          <div className="flex justify-between items-center py-3 border-b border-gray-700">
            <span className="text-gray-300 font-medium">Next Billing Date</span>
            <span className="text-white">{formatDate(nextBilledAt)}</span>
          </div>
        )}
      </div>

      <div className="mb-6 flex items-start gap-3 rounded-md border border-gray-700 bg-gray-900/60 px-4 py-3">
        <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        <p className="text-sm text-gray-400">
          Paddle is our merchant of record and handles every subscription change — payment details,
          plan changes and cancellation all happen on Paddle&apos;s secure pages.
          {hasManagementUrls
            ? ' The links below open them in a new tab.'
            : ' Use the link in your Paddle receipt email, or contact support and we\'ll send you one.'}
        </p>
      </div>

      <div className="space-y-3">
        {managementUrls.updatePaymentMethod && (
          <a
            href={managementUrls.updatePaymentMethod}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between w-full rounded-md bg-primary-600 px-4 py-3 text-white hover:bg-primary-700 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <CreditCardIcon className="h-5 w-5" />
              <span className="font-medium">Update Payment Method</span>
            </div>
            <ExternalLinkIcon className="h-4 w-4 opacity-70 group-hover:opacity-100" />
          </a>
        )}

        {managementUrls.cancel && (
          <a
            href={managementUrls.cancel}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between w-full rounded-md bg-gray-700 px-4 py-3 text-white hover:bg-red-600 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <XCircleIcon className="h-5 w-5" />
              <span className="font-medium">Cancel Subscription</span>
            </div>
            <ExternalLinkIcon className="h-4 w-4 opacity-70 group-hover:opacity-100" />
          </a>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManagementCard;
